/* eslint-disable no-shadow */
import { computed, readonly, ref } from 'vue';
import { domain } from '@/util/domainUtils';

// AKS
import aksDemo from '@/configs/aks/demo.json';
import aksDev from '@/configs/aks/dev.json';
import aksTest from '@/configs/aks/test.json';
// SE
import akademikerforsakring from '@/configs/se/akademikerforsakring.json';
import seDev from '@/configs/se/dev.json';
import seProd from '@/configs/se/prod.json';
import raksystems from '@/configs/se/raksystems.json';
import digitaltjanstforjuridiskadokument from '@/configs/se/digitaltjanstforjuridiskadokument.json';
import sustera from '@/configs/se/sustera.json';
// UK
import ukDev from '@/configs/uk/dev.json';
import documentcentre from '@/configs/uk/documentcentre.json';
import ukProd from '@/configs/uk/prod.json';
// IE
import ieProd from '@/configs/ie/prod.json';
// IT
import itProd from '@/configs/it/prod.json';
import itProdPos from '@/configs/it/pos.json';
import itCardif from '@/configs/it/cardif.json';
import itHdi from '@/configs/it/hdi.json';
import itHelvetia from '@/configs/it/helvetia.json';
import itItaliana from '@/configs/it/italiana.json';
import itMediolanum from '@/configs/it/mediolanum.json';
import itPosteassicura from '@/configs/it/posteassicura.json';
import itReale from '@/configs/it/reale.json';
import itUnipol from '@/configs/it/unipol.json';
import itVera from '@/configs/it/vera.json';
// AU
import auProd from '@/configs/au/prod.json';
import auProdMain from '@/configs/au/prod-main.json';
// SL
import slProd from '@/configs/sl/prod.json';
// CA
import caProd from '@/configs/ca/prod.json';
// DE
import deProd from '@/configs/de/prod.json';

export enum ARAG_DOMAINS {
  AKS_DEMO = 'demo.aks.dc.arag.com',
  AKS_DEV = 'dev.aks.dc.arag.com',
  AKS_TEST = 'test.aks.dc.arag.com',
  SE_AKADEMIKERFORSAKRING = 'akademikerforsakring.arag.se',
  SE_DEV = 'se.dev.aks.dc.arag.com',
  SE_PROD = 'se.prod.aks.dc.arag.com',
  SE_RAKSYSTEMS = 'raksystems.arag.se',
  SE_DIGITALTJANSTFORJURIDISKADOKUMENT = 'digitaltjanstforjuridiskadokument.arag.se',
  SE_SUSTERA = 'sustera.arag.se',
  UK_DEV = 'uk.dev.aks.dc.arag.com',
  UK_DOCUMENTCENTER = 'documentcentre.arag.co.uk',
  UK_PROD = 'uk.prod.aks.dc.arag.com',
  IE_PROD = 'ie.prod.aks.dc.arag.com',
  IE_DOCUMENTCENTER = 'legaldocuments.arag.ie',
  IT_PROD = 'it.prod.aks.dc.arag.com',
  IT_PROD_NEW = 'documentcenter.arag.it',
  IT_PROD_POS = 'documentcenterpos.arag.it',
  IT_HDI = 'hdidocs.arag.it',
  IT_HELVETIA = 'helvetiadocs.arag.it',
  IT_VERA = 'veradocs.arag.it',
  IT_UNIPOL = 'unipoldocs.arag.it',
  IT_POSTEASSICURA = 'posteassicuradocs.arag.it',
  IT_REALE = 'realedocs.arag.it',
  IT_ITALIANA = 'italianadocs.arag.it',
  IT_CARDIF = 'cardifdocs.arag.it',
  IT_MEDIOLANUM = 'mediolanumdocs.arag.it',
  AU_PROD = 'au.prod.aks.dc.arag.com',
  AU_PROD_NEW = 'documentcentre-new.arag.com.au',
  AU_PROD_MAIN = 'documentcentre.arag.com.au',
  SL_PROD = 'sl.prod.aks.dc.arag.com',
  SL_PROD_NEW = 'documentcentre.arag.si',
  SL_PROD_NEW_2 = 'new.documentcentre.arag.si',
  CA_PROD = 'documentcentre.arag.ca',
  CA_PROD_NEW = 'new.documentcentre.arag.ca',
  DE_PROD = 'de.prod.aks.dc.arag.com',
}

export default function useConfigs() {
  const environment = ref('');

  const configs = computed(() => {
    switch (domain) {
      case ARAG_DOMAINS.AKS_DEMO:
        environment.value = 'aks';
        return aksDemo;
      case ARAG_DOMAINS.AKS_DEV:
        environment.value = 'aks';
        return aksDev;
      case ARAG_DOMAINS.AKS_TEST:
        environment.value = 'aks';
        return aksTest;
      case ARAG_DOMAINS.SE_AKADEMIKERFORSAKRING:
        environment.value = 'se';
        return akademikerforsakring;
      case ARAG_DOMAINS.SE_DEV:
        environment.value = 'se';
        return seDev;
      case ARAG_DOMAINS.SE_PROD:
        environment.value = 'se';
        return seProd;
      case ARAG_DOMAINS.SE_RAKSYSTEMS:
        environment.value = 'se';
        return raksystems;
      case ARAG_DOMAINS.SE_DIGITALTJANSTFORJURIDISKADOKUMENT:
        environment.value = 'se';
        return digitaltjanstforjuridiskadokument;
      case ARAG_DOMAINS.SE_SUSTERA:
        environment.value = 'se';
        return sustera;
      case ARAG_DOMAINS.UK_DEV:
        environment.value = 'uk';
        return ukDev;
      case ARAG_DOMAINS.UK_DOCUMENTCENTER:
        environment.value = 'uk';
        return documentcentre;
      case ARAG_DOMAINS.UK_PROD:
        environment.value = 'uk';
        return ukProd;
      case ARAG_DOMAINS.IE_PROD:
      case ARAG_DOMAINS.IE_DOCUMENTCENTER:
        environment.value = 'ie';
        return ieProd;
      case ARAG_DOMAINS.IT_CARDIF:
        environment.value = 'it';
        return itCardif;
      case ARAG_DOMAINS.IT_HDI:
        environment.value = 'it';
        return itHdi;
      case ARAG_DOMAINS.IT_HELVETIA:
        environment.value = 'it';
        return itHelvetia;
      case ARAG_DOMAINS.IT_ITALIANA:
        environment.value = 'it';
        return itItaliana;
      case ARAG_DOMAINS.IT_MEDIOLANUM:
        environment.value = 'it';
        return itMediolanum;
      case ARAG_DOMAINS.IT_POSTEASSICURA:
        environment.value = 'it';
        return itPosteassicura;
      case ARAG_DOMAINS.IT_REALE:
        environment.value = 'it';
        return itReale;
      case ARAG_DOMAINS.IT_UNIPOL:
        environment.value = 'it';
        return itUnipol;
      case ARAG_DOMAINS.IT_VERA:
        environment.value = 'it';
        return itVera;
      case ARAG_DOMAINS.IT_PROD:
      case ARAG_DOMAINS.IT_PROD_NEW:
        environment.value = 'it';
        return itProd;
      case ARAG_DOMAINS.IT_PROD_POS:
        environment.value = 'it';
        return itProdPos;
      case ARAG_DOMAINS.AU_PROD_NEW:
      case ARAG_DOMAINS.AU_PROD_MAIN:
        environment.value = 'au';
        return auProdMain;
      case ARAG_DOMAINS.AU_PROD:
        environment.value = 'au';
        return auProd;
      case ARAG_DOMAINS.SL_PROD:
      case ARAG_DOMAINS.SL_PROD_NEW:
      case ARAG_DOMAINS.SL_PROD_NEW_2:
        environment.value = 'sl';
        return slProd;
      case ARAG_DOMAINS.CA_PROD_NEW:
      case ARAG_DOMAINS.CA_PROD:
        environment.value = 'ca';
        return caProd;
      case ARAG_DOMAINS.DE_PROD:
        environment.value = 'de';
        return deProd;
      default:
        environment.value = 'localhost';
        return aksDev;
    }
  });

  return {
    ...readonly(configs.value),
    environment,
  };
}
